import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, MessageCircle, Phone, MapPin, Mail } from "lucide-react";
import useConfig from "@/lib/useConfig";

const SOCIALS = [
  { name: "WhatsApp", href: "https://wa.me/918800127385", icon: MessageCircle, testid: "footer-social-whatsapp" },
  { name: "Instagram", href: "https://www.instagram.com/medipats/", icon: Instagram, testid: "footer-social-instagram" },
  { name: "Facebook", href: "https://www.facebook.com/share/19brovBz6S/?mibextid=wwXIfr", icon: Facebook, testid: "footer-social-facebook" },
  { name: "LinkedIn", href: "https://www.linkedin.com/company/medipats/", icon: Linkedin, testid: "footer-social-linkedin" },
];

export default function Footer() {
  const { email, waLink } = useConfig();
  return (
    <footer className="bg-[#0B0E1A] border-t border-[#5E3A5C]/30 text-[#F3E9EC]/70 pt-16 pb-10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <img src="/medipats-logo.png" alt="MediPats" className="w-12 h-12 object-contain"/>
            <span className="font-serif text-2xl text-[#F3E9EC]">MediPats</span>
          </div>
          <p className="text-sm leading-relaxed">
            International healthcare concierge for patients travelling to India for medical treatment.
          </p>
          <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
            {SOCIALS.map(s => (
              <a
                key={s.name}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                aria-label={s.name}
                title={s.name}
                data-testid={s.testid}
                className="w-9 h-9 rounded-full flex items-center justify-center border border-[#5E3A5C]/40 text-[#F3E9EC]/70 hover:text-white hover:border-[#B47A9A]/70 hover:bg-[#B47A9A]/10 transition-colors"
              >
                <s.icon className="w-4 h-4"/>
              </a>
            ))}
          </div>
        </div>
        <div>
          <h4 className="overline mb-4">Explore</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/treatments">Treatments</Link></li>
            <li><Link to="/hospitals">Hospitals</Link></li>
            <li><Link to="/doctors">Doctors</Link></li>
            <li><Link to="/countries">Countries</Link></li>
            <li><Link to="/costs">Estimated Costs</Link></li>
            <li><Link to="/blog">Blog</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="overline mb-4">Company</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/partner">Partner With Us</Link></li>
            <li><Link to="/enquiry">Get Personalised Cost</Link></li>
            <li><a href={waLink()} target="_blank" rel="noreferrer">WhatsApp</a></li>
          </ul>
          <h4 className="overline mb-4 mt-6">Contact</h4>
          <ul className="space-y-3 text-sm" data-testid="footer-contact">
            <li>
              <a href="tel:+918800127385" className="flex items-start gap-2.5 hover:text-white transition-colors" data-testid="footer-phone">
                <Phone className="w-4 h-4 text-[#B47A9A] mt-0.5 flex-shrink-0"/>
                <span>+91 8800127385</span>
              </a>
            </li>
            <li>
              <a href={`mailto:${email}`} className="flex items-start gap-2.5 hover:text-white transition-colors" data-testid="footer-email">
                <Mail className="w-4 h-4 text-[#B47A9A] mt-0.5 flex-shrink-0"/>
                <span className="break-all">{email}</span>
              </a>
            </li>
            <li>
              <div className="flex items-start gap-2.5" data-testid="footer-address">
                <MapPin className="w-4 h-4 text-[#B47A9A] mt-0.5 flex-shrink-0"/>
                <address className="not-italic leading-relaxed text-[#F3E9EC]/70">
                  524, 5th Floor, Seminar Block,<br/>
                  IIIT Delhi Innovation and<br/>
                  Incubation Center,<br/>
                  New Delhi, Delhi – 110020
                </address>
              </div>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="overline mb-4">Disclaimer</h4>
          <p className="text-xs leading-relaxed">
            MediPats is a medical-travel concierge and is not a hospital. We do not provide
            medical treatment or diagnoses. All treatment decisions must be made by qualified
            medical professionals. Indicative costs may vary based on individual patient conditions.
          </p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-12 pt-6 border-t border-[#5E3A5C]/25 flex flex-col md:flex-row justify-between text-xs text-[#F3E9EC]/45">
        <span>© {new Date().getFullYear()} MediPats. All rights reserved.</span>
        <span>Built for international patients travelling to India.</span>
      </div>
    </footer>
  );
}
